import { FC, PropsWithChildren, ReactNode, useId } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { useFormatter } from '@eneco/i18n';
import { Box, Divider, RadioCard, Stack, Stretch, Text } from '@eneco/sparky';

import { useRibbonHeightSync } from './useRibbonHeightSync';
import { useTitleHeightSync } from './useTitleHeightSync';
import { Price as PriceType } from '../../../../shared/types';

interface Props {
  id: string;
  title?: string;
  subtitle?: string;
  ribbon?: ReactNode;
  touRibbon?: ReactNode;

  /** Headline price the customer effectively pays per month, including any discount. */
  price?: PriceType;
  /** Optional undiscounted monthly term amount ("Termijnbedrag"), shown next to the headline. */
  secondaryPrice?: PriceType;

  usps?: string[];
}

const PriceColumn: FC<{ price: PriceType; id?: string; isHeadline?: boolean }> = ({ price, id, isHeadline }) => {
  const { currency } = useFormatter();
  const formatted = currency.euro(price.value);

  return (
    <Stack direction="column" gap="0" alignX={isHeadline ? 'start' : 'end'}>
      <Text size={isHeadline ? 'BodyXL' : 'BodyM'} weight="bold">
        <span id={id} aria-label={[formatted, price.period].filter(Boolean).join(' ')}>
          {formatted}
        </span>
      </Text>
      {price.period && (
        <Text size="BodyS" color="textLowEmphasis">
          {price.period}
        </Text>
      )}
    </Stack>
  );
};

/**
 * Experiment exp-n-575 product card: improved UI with title + subtitle, a dual price
 * (discounted "Per maand inclusief korting" headline and a "Termijnbedrag" secondary), the
 * price build-up expandable above the USP checklist, and the radio indicator in the top-right.
 */
export const ProductCard: FC<PropsWithChildren<Props>> = ({
  id: productId,
  title,
  subtitle,
  ribbon,
  touRibbon,
  price,
  secondaryPrice,
  usps,
  children,
}) => {
  const id = useId();
  const { titleRef, wrapperRef } = useTitleHeightSync(!!title);
  const { ribbonRef, ribbonWrapperRef } = useRibbonHeightSync();

  // The card is laid out as a flex column of two rows: a ribbon slot ABOVE the card, then the card
  // itself. Rendering the ribbon outside the RadioCard (whose inner overlay clips overflow) lets it
  // overhang above the card. The slot is reserved to the tallest sibling ribbon height so every
  // card's body starts on the same top line; a card without a ribbon just leaves the slot as
  // transparent space above it, rather than white space inside the card.
  return (
    <Stack direction="column">
      {/* Ribbon slot. `ribbonWrapperRef` reserves the shared tallest-ribbon height (see
          useRibbonHeightSync); the inner `ribbonRef` measures only the real ribbon (0 when absent,
          so it never inflates the shared max). `alignY="end"` drops the ribbon to the bottom of the
          slot so its bottom edge meets the card's top edge. */}
      <Stack ref={ribbonWrapperRef} direction="column" alignY="end">
        <Box ref={ribbonRef}>
          {ribbon && (
            <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
              {ribbon}
            </Box>
          )}

          {touRibbon && !ribbon && (
            <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
              {touRibbon}
            </Box>
          )}
        </Box>
      </Stack>

      {/* Card region grows to fill the remaining height so the cards stay equal height. The row
          Stack makes the RadioCard wrapper stretch to full height (cross axis) and `Stretch` fills
          its inner overlay. NOTE: keep the wrapper component stable (always `Stretch`) — swapping
          the wrapper element type when the price-details expandable opens remounts the Radix
          Collapsible inside `children`, which suppresses its slide-open animation. */}
      <Stack.Item grow>
        <Stretch>
          <Stack direction="row">
            <Stretch>
              <RadioCard value={productId} ariaLabelledby={`${id}-label ${id}-price`}>
                <Stretch>
                  <Stack direction="column">
                    {/* NOTE: intentionally NOT `grow` here. Cards are stretched to equal height, so
                        growing the header would absorb the slack in shorter cards and push the
                        expandable row down, opening a large gap between the price and the expandable.
                        Keeping the header at its natural height makes the price + expandable rows
                        line up across cards, matching the design; the trailing whitespace falls at
                        the bottom of the card instead. */}
                    <Stack.Item>
                      <Box padding="6">
                        <Stack gap="4">
                          <Stack direction="row" alignX="justify" alignY="start" gap="2">
                            <Stack direction="column" gap="0">
                              {title && (
                                // Reserve the tallest title height measured across the sibling cards
                                // (see useTitleHeightSync) so a single-line title occupies the same
                                // height as a wrapping one, keeping the subtitle, price and expandable
                                // rows aligned across all offer cards. `wrapperRef` gets the reserved
                                // height; the inner `titleRef` stays unconstrained so its natural
                                // height is measured.
                                <div ref={wrapperRef}>
                                  <div ref={titleRef}>
                                    <Text id={`${id}-label`} weight="bold">
                                      {title}
                                    </Text>
                                  </div>
                                </div>
                              )}
                              {subtitle && (
                                <Text size="BodyS" color="textLowEmphasis">
                                  {subtitle}
                                </Text>
                              )}
                            </Stack>

                            <Stack.Item shrink={false}>
                              <RadioCard.Indicator />
                            </Stack.Item>
                          </Stack>

                          {price && (
                            <Stack direction="row" alignX="justify" alignY="start" gap="4">
                              <PriceColumn price={price} id={`${id}-price`} isHeadline />
                              {secondaryPrice && <PriceColumn price={secondaryPrice} />}
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    </Stack.Item>

                    {children && (
                      <Stack.Item>
                        <Divider />
                        {children}
                      </Stack.Item>
                    )}

                    {usps && usps.length > 0 && (
                      <Stack.Item>
                        <Divider />
                        <Box padding="6">
                          <Checklist>
                            {usps.map(usp => (
                              <Checklist.Item key={usp} gap="2" alignY="start">
                                <Stack>
                                  <RichText html={usp} />
                                </Stack>
                              </Checklist.Item>
                            ))}
                          </Checklist>
                        </Box>
                      </Stack.Item>
                    )}
                  </Stack>
                </Stretch>
              </RadioCard>
            </Stretch>
          </Stack>
        </Stretch>
      </Stack.Item>
    </Stack>
  );
};
